import React from 'react';
import ReactMarkdown from 'react-markdown';
import AssetCard from '../Cards/AssetCard';
import VideoScrubPreview from '../Cards/VideoScrubPreview';
import QCReportCard from '../Cards/QCReportCard';
import ThinkingIndicator from './ThinkingIndicator';
import { PlayCircle, Image as ImageIcon, ExternalLink } from 'lucide-react';

interface MessageListProps {
  messages: Array<{
    role: 'user' | 'assistant';
    content: string;
    type?: string;
    data?: any;
    assets?: any[];
  }>;
  onQC?: (assetId: string) => void;
  thinkingStatus?: string | null;
}

/**
 * Try to extract an asset ID from a URL.
 * Matches patterns like /preview/12345 or id=12345
 */
function extractAssetId(href: string): string | null {
  // /preview/{id} pattern (artstash_preview_url)
  const previewMatch = href.match(/\/preview\/(\d+)/);
  if (previewMatch) return previewMatch[1];
  // ?id={id} pattern
  const paramMatch = href.match(/[?&]id=(\d+)/);
  if (paramMatch) return paramMatch[1];
  return null;
}

/** Find a matching asset by URL or extracted ID */
function findAsset(assets: any[] | undefined, href: string): any | null {
  if (!assets || !href) return null;

  // Direct URL match
  const direct = assets.find((a: any) =>
    a.artstash_preview_url === href ||
    a.sas_url === href ||
    a.url === href ||
    a.contentUrl === href
  );
  if (direct) return direct;

  // Match by asset ID extracted from URL
  const extractedId = extractAssetId(href);
  if (extractedId) {
    return assets.find((a: any) => String(a.id) === extractedId);
  }

  return null;
}

/** Inline image component for matched assets */
const InlineAssetImage: React.FC<{ asset: any; linkText: string }> = ({ asset, linkText }) => {
  const [error, setError] = React.useState(false);
  const mediaType = (asset.media_type || asset.asset_type || '').toLowerCase();
  const isImage = mediaType === 'image';
  // Treat empty strings as null
  const thumbUrl = asset.thumbnail_url?.trim() || asset.thumbnailUrl?.trim() || null;
  const imageSrc = thumbUrl || (isImage ? asset.sas_url : null);
  const displayName = asset.title || asset.name || linkText || 'Asset';

  // For video/3D assets, use sas_url for hover-to-scrub
  const videoScrubUrl = (mediaType === 'video' || mediaType === 'model_3d' || mediaType === '3d') ? (asset.sas_url?.trim() || null) : null;

  return (
    <span className="inline-asset" style={{ display: 'block', margin: '0.75rem 0' }}>
      <a
        href={asset.artstash_preview_url || asset.sas_url || '#'}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-asset-link"
      >
        {videoScrubUrl ? (
          <span className="inline-asset-image-wrapper">
            <VideoScrubPreview
              thumbnailUrl={imageSrc || undefined}
              videoUrl={videoScrubUrl}
              alt={displayName}
            />
          </span>
        ) : imageSrc && !error ? (
          <span className="inline-asset-image-wrapper">
            <img
              src={imageSrc}
              alt={displayName}
              className="inline-asset-image"
              onError={() => setError(true)}
            />
            {mediaType === 'video' && (
              <span className="inline-asset-video-badge">
                <PlayCircle size={20} />
              </span>
            )}
          </span>
        ) : (
          <span className="inline-asset-placeholder">
            {mediaType === 'video' ? <PlayCircle size={32} /> : <ImageIcon size={32} />}
          </span>
        )}
        <span className="inline-asset-caption">
          <span className="inline-asset-name">{displayName}</span>
          <ExternalLink size={12} style={{ opacity: 0.5 }} />
        </span>
      </a>
      {asset.tags && asset.tags.length > 0 && (
        <span className="inline-asset-tags">
          {asset.tags.slice(0, 5).map((tag: string) => (
            <span key={tag} className="tag">{tag}</span>
          ))}
        </span>
      )}
    </span>
  );
};

const MessageList: React.FC<MessageListProps> = ({ messages, onQC = () => {}, thinkingStatus }) => {
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinkingStatus]);
  return (
    <div className="messages-list" style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {messages.map((msg, index) => (
        <div key={index} className={`message ${msg.role}`} style={{ alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
          <div className="message-bubble" style={{
            background: msg.role === 'user' ? '#8b5cf6' : '#1a1a1e',
            color: msg.role === 'user' ? '#ffffff' : '#e5e7eb',
            padding: '0.75rem',
            borderRadius: '12px',
            border: msg.role === 'user' ? 'none' : '1px solid #2e2e2e'
          }}>
            {msg.type === 'trace' && (
              <div style={{ fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic', marginBottom: '0.5rem' }}>
                {msg.content}
              </div>
            )}

            {(msg.type === 'text' || !msg.type) && (
              <>
                <ReactMarkdown
                  components={{
                    a: ({node, children, ...props}) => {
                      const href = props.href || '';
                      const asset = findAsset(msg.assets, href);

                      if (asset) {
                        const linkText = typeof children === 'string' ? children : '';
                        return <InlineAssetImage key={`asset-${asset.id}`} asset={asset} linkText={linkText} />;
                      }
                      return <a {...props} target="_blank" rel="noopener noreferrer">{children}</a>;
                    }
                  }}
                >
                  {msg.content}
                </ReactMarkdown>
              </>
            )}

            {msg.type === 'assets' && (
              <div className="assets-grid">
                {msg.data?.map((asset: any) => (
                  <AssetCard key={asset.id} asset={asset} onQC={onQC} />
                ))}
              </div>
            )}

            {msg.type === 'qc-report' && (
              <QCReportCard report={msg.data} />
            )}
          </div>
        </div>
      ))}
      {thinkingStatus && <ThinkingIndicator status={thinkingStatus} />}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default React.memo(MessageList);
